// ── TmRecentContracts — N개월 내 계약현황 (TM 콜 방지용 전사 공유) ──────────────
// - 관리자가 설정한 N개월 이내 계약된 업체 목록 표시
// - 표시 컬럼: 계약일 / 상호명 / 주소
// - 목적: 일일콜현황 직원이 최근 계약된 업체에 전화하지 않도록 공유

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { apiClient } from '../../api/apiClient';
import { useToast } from '../../components/Toast';

// ── 날짜 포맷 (YYYY-MM-DD → YYYY.MM.DD) ──────────────────────────────────────
function fmtDate(v) { return v ? String(v).replace(/-/g, '.') : '-'; }

// ── 테이블 스타일 ─────────────────────────────────────────────────────────────
const TH = {
  padding: '8px 12px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)',
  background: 'var(--bg-subtle)', borderBottom: '2px solid var(--border)',
  whiteSpace: 'nowrap', textAlign: 'left',
};
const TD = {
  padding: '8px 12px', fontSize: 13, color: 'var(--text-sub)',
  borderBottom: '1px solid var(--border)', verticalAlign: 'middle',
};

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────────
export default function TmRecentContracts() {
  const toast = useToast();
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [months,  setMonths]  = useState(null); // 서버에서 받아온 N값
  const [query,   setQuery]   = useState('');   // 지역(주소) 검색어

  // 검색 시: 주소에 검색어가 포함된 행만 + 계약일 내림차순. 미검색 시: 원래 순서 유지.
  const visibleRows = useMemo(() => {
    const q = query.trim();
    if (!q) return rows;
    return rows
      .filter((r) => String(r.address || '').includes(q))
      .slice()
      .sort((a, b) => String(b.contract_date || '').localeCompare(String(a.contract_date || '')));
  }, [rows, query]);

  // 모바일 감지
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── 데이터 조회 ─────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/tm/recent-contracts');
      if (res.data?.success) {
        setRows(res.data.data || []);
        setMonths(res.data.months ?? null);
      } else {
        toast.error(res.data?.message || '계약 목록을 불러오지 못했습니다.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || '계약 목록 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── 제목: N개월 내 계약현황 ────────────────────────────────────────────────
  const title = months != null ? `${months}개월 내 계약현황` : '계약현황';

  // ── 로딩 ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: '20px 0' }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{title}</h2>
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)', fontSize: 14 }}>
          불러오는 중...
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 0' }}>
      {/* ── 헤더 ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{title}</h2>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {query.trim() ? `${visibleRows.length}/${rows.length}건` : `${rows.length}건`}
        </span>
        {/* 지역(주소) 검색창 */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
          <span style={{ position: 'absolute', left: 9, fontSize: 13, color: 'var(--text-muted)', pointerEvents: 'none' }}>🔍</span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="지역/주소 검색"
            style={{
              paddingLeft: 28, paddingRight: query ? 26 : 10, height: 34, fontSize: 13,
              border: '1px solid var(--border)', borderRadius: 8, outline: 'none',
              width: 200, maxWidth: '60vw', background: 'var(--bg-card)', color: 'var(--text)',
            }}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              style={{ position: 'absolute', right: 6, border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, padding: 0, lineHeight: 1 }}
            >✕</button>
          )}
        </div>
      </div>

      {/* ── 빈 목록 ─────────────────────────────────────────────────────── */}
      {visibleRows.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)', fontSize: 14,
          background: 'var(--bg-subtle)', borderRadius: 8, border: '1px dashed var(--border)',
        }}>
          {query.trim()
            ? '검색 결과가 없습니다.'
            : (months != null ? `최근 ${months}개월 이내 계약이 없습니다.` : '계약이 없습니다.')}
        </div>
      )}

      {/* ── 모바일: 카드 ────────────────────────────────────────────────── */}
      {visibleRows.length > 0 && isMobile && (
        <div style={{ display: 'grid', gap: 8 }}>
          {visibleRows.map((row) => (
            <div key={row.id} style={{
              border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px',
              background: 'var(--bg-card)',
            }}>
              {/* 계약일 */}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 5 }}>
                {fmtDate(row.contract_date)}
              </div>
              {/* 상호명 */}
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
                {row.company_name || '-'}
              </div>
              {/* 주소 */}
              <div style={{ fontSize: 12, color: 'var(--text-sub)' }}>
                {row.address || '주소 없음'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── 데스크탑: 테이블 ─────────────────────────────────────────────── */}
      {visibleRows.length > 0 && !isMobile && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-card)' }}>
            <thead>
              <tr>
                <th style={TH}>계약일</th>
                <th style={TH}>상호명</th>
                <th style={{ ...TH, width: '100%' }}>주소</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.id}>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{fmtDate(row.contract_date)}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap', maxWidth: 192, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.company_name || '-'}
                  </td>
                  <td style={{ ...TD, color: 'var(--text-muted)' }}>
                    {row.address || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
